"use strict";
/**
 * Ingredient Category Detection — shared constants
 *
 * Maps lowercase INCI substrings to ingredient categories. Consumed by:
 *   - backend: weekly/skin report generation (timeline computation,
 *     suggestion curation, routine optimization). The backend wraps this
 *     with its own MECHANISM_MATRIX-filtered extraction function for
 *     efficacy-weeks lookup.
 *   - frontend: paywall routine coverage detection.
 *
 * Single source of truth for the keyword map lives here. Extraction can
 * be either unfiltered (this file) or matrix-filtered (backend-local).
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.INGREDIENT_CATEGORY_KEYWORDS = void 0;
exports.extractIngredientCategories = extractIngredientCategories;
exports.INGREDIENT_CATEGORY_KEYWORDS = {
    // === ANTI-AGING / CELL TURNOVER ===
    // 'retinyl' (retinyl palmitate/retinoate) and 'retinoate'
    // (hydroxypinacolone retinoate / granactive retinoid) are vitamin-A
    // esters — weaker retinoids but still pregnancy-contraindicated. Added
    // in the 2026-07-05 safety audit: ~2,600 products in pdb_product carry
    // one of these with no free-retinol keyword and were otherwise missed.
    retinoid: [
        'retinol',
        'retinaldehyde',
        'retinal',
        'tretinoin',
        'adapalene',
        'retinyl',
        'retinoate',
    ],
    bakuchiol: ['bakuchiol'],
    peptides: ['peptide'],
    // === EXFOLIANTS ===
    aha: ['glycolic acid', 'tartaric acid'],
    bha: ['salicylic acid', 'beta hydroxy'],
    salicylic_acid: ['salicylic acid', 'beta hydroxy'],
    lactic_acid: ['lactic acid'],
    mandelic_acid: ['mandelic acid'],
    pha: ['gluconolactone', 'lactobionic acid', 'polyhydroxy'],
    // === BRIGHTENING / PIGMENTATION ===
    vitamin_c: ['ascorbic acid', 'vitamin c', 'l-ascorbic', 'ascorbyl'],
    niacinamide: ['niacinamide', 'nicotinamide', 'vitamin b3'],
    tranexamic_acid: ['tranexamic acid'],
    arbutin: ['arbutin'],
    kojic_acid: ['kojic acid'],
    licorice_extract: ['licorice', 'glycyrrhiza', 'glabridin'],
    // === ACNE / ANTI-INFLAMMATORY ===
    benzoyl_peroxide: ['benzoyl peroxide'],
    azelaic_acid: ['azelaic acid'],
    sulfur: ['sulfur', 'sulphur'],
    zinc: ['zinc pca', 'zinc gluconate'],
    // === BARRIER / HYDRATION ===
    hyaluronic_acid: ['hyaluronic acid', 'sodium hyaluronate'],
    ceramides: ['ceramide'],
    squalane: ['squalane', 'squalene'],
    glycerin: ['glycerin', 'glycerol'],
    panthenol: ['panthenol', 'pantothenic', 'provitamin b5', 'dexpanthenol'],
    // === CALMING / ANTI-REDNESS ===
    centella: ['centella', 'cica', 'madecassoside', 'asiaticoside'],
    madecassoside: ['madecassoside'],
    aloe_vera: ['aloe barbadensis', 'aloe vera'],
    allantoin: ['allantoin'],
    // === EYE AREA ===
    caffeine: ['caffeine'],
    vitamin_k: ['phytonadione', 'vitamin k'],
    // === ANTIOXIDANTS ===
    vitamin_e: ['tocopherol', 'tocopheryl', 'vitamin e'],
    ferulic_acid: ['ferulic acid'],
    resveratrol: ['resveratrol'],
    coq10: ['ubiquinone', 'coenzyme q10', 'coq10'],
    green_tea: ['camellia sinensis', 'green tea', 'epigallocatechin', 'egcg'],
    // === FERMENTED INGREDIENTS ===
    galactomyces: ['galactomyces', 'pitera', 'saccharomyces'],
    fermented_rice: ['rice ferment', 'sake', 'aspergillus/rice'],
    probiotics: ['lactobacillus', 'bifidobacterium', 'probiotic', 'lysate'],
    snail_mucin: ['snail secretion', 'snail mucin', 'helix aspersa'],
    // === ADAPTOGENS & BOTANICALS ===
    mushroom_extract: ['tremella', 'reishi', 'chaga', 'ganoderma', 'mushroom'],
    turmeric: ['turmeric', 'curcuma longa', 'curcumin'],
    sea_buckthorn: ['hippophae', 'sea buckthorn'],
    // === SUN PROTECTION ===
    sunscreen: [
        'avobenzone',
        'oxybenzone',
        'octinoxate',
        'zinc oxide',
        'titanium dioxide',
    ],
};
/**
 * Case-insensitive substring check for a single category.
 */
function detectIngredientCategory(ingredientsText, category) {
    const lowerIngredients = ingredientsText.toLowerCase();
    const keywords = exports.INGREDIENT_CATEGORY_KEYWORDS[category];
    return keywords.some((k) => lowerIngredients.includes(k));
}
/**
 * Unfiltered extraction — returns every category from the keyword map
 * whose substrings match the ingredient text. Use this when you want
 * ALL detected categories regardless of any downstream filter.
 *
 * Backend uses its own MECHANISM_MATRIX-filtered variant for timeline
 * computation; that one stays local to the backend.
 */
function extractIngredientCategories(ingredientsText) {
    const categories = [];
    for (const category of Object.keys(exports.INGREDIENT_CATEGORY_KEYWORDS)) {
        if (detectIngredientCategory(ingredientsText, category)) {
            categories.push(category);
        }
    }
    return categories;
}
//# sourceMappingURL=ingredientCategories.js.map